import { DateTime } from "luxon";
import { z } from "zod/v4";

import { Voucher } from "../schema";

const BaseVoucherSchema = z.object({
  uuid: z.string(),
  voucherCartUuid: z.string(),
  voucherTemplateUuid: z.string().nullable(),
  voucherName: z.string().nullable(),
  quantity: z.number().int().min(1, "Quantity must be at least 1").default(1),
  priceInCents: z.number().int().min(0, "Price must be a non-negative integer"),
  message: z.string().nullable(),
  valueInCents: z.number().int().min(0, "Value must be a non-negative integer"),

  recipientEmail: z.string().nullable(),
  senderName: z.string().nullable(),
});
const SelfPurchaseVoucher = BaseVoucherSchema.extend({
  creationReason: z.literal("self_purchase"),
  recipientName: z.string().nullable(),
});

const GiftVoucher = BaseVoucherSchema.extend({
  creationReason: z.literal("gift_for_someone_else"),
  recipientName: z.string(),
  recipientEmail: z.email(),
  senderName: z.string(),
  message: z.string().optional(),
});
const UnknownRecipientVoucher = BaseVoucherSchema.extend({
  recipientName: z.string().nullable(),
  creationReason: z.literal("gift_for_unknown_recipient"),
  message: z.string().nullish(),
  senderName: z.string(),
});

export const VoucherCartItemSchema = z
  .discriminatedUnion("creationReason", [
    SelfPurchaseVoucher,
    GiftVoucher,
    UnknownRecipientVoucher,
  ])
  .transform((item) => ({
    ...item,
    value: item.valueInCents / 100,
  }));

export const VoucherCartItemsSchema = z.array(VoucherCartItemSchema);
export type VoucherCartItem = z.infer<typeof VoucherCartItemSchema>;

export type MinimalVoucher = Pick<
  Voucher,
  | "recipientName"
  | "giftMessage"
  | "venueName"
  | "openingBalanceInCents"
  | "senderName"
  | "voucherName"
  | "balanceInCents"
  | "qrCode"
  | "expiresAt"
  | "code"
>;
export const convertToMinimalVoucher = (
  voucher: z.infer<typeof VoucherCartItemSchema>,
  venueName: string,
): MinimalVoucher => ({
  recipientName: voucher.recipientName || "",
  giftMessage: voucher.message || "",
  voucherName: voucher.voucherName,
  venueName, // Assuming venueName is not part of VoucherCartItemSchema
  openingBalanceInCents: voucher.valueInCents,
  balanceInCents: voucher.valueInCents,
  qrCode: "Gotta buy me first", // Assuming qrCode is not part of VoucherCartItemSchema
  expiresAt: DateTime.now().plus({ years: 3 }).toISO(),
  senderName: voucher.senderName || "",
  code: "BUY-ME-FIRST 😜", // Assuming code is not part of VoucherCartItemSchema
});
